import React, { useState } from 'react';
import { BriefcaseIcon, ClockIcon, DollarSignIcon, BookOpenIcon, CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, TrendingUpIcon, GlobeIcon, ShieldCheckIcon } from 'lucide-react';
import { degreeInterestOptions, focusOptions, specializationOptions } from '../data/degrees';
interface ProgramContentSectionProps {
  degreeInterest: string;
  areaOfStudy: string;
  specialization: string;
}
// Helper to generate realistic careers based on area of study
const getCareersForArea = (areaValue: string, programName: string) => {
  const defaultCareers = [{
    title: `${programName} Specialist`,
    desc: `Apply your knowledge of ${programName.toLowerCase()} in various professional settings.`,
    salary: '$55,000 - $85,000'
  }, {
    title: 'Project Coordinator',
    desc: 'Manage teams and deliverables across different organizational departments.',
    salary: '$60,000 - $90,000'
  }, {
    title: 'Operations Analyst',
    desc: 'Improve efficiency and processes within your chosen industry.',
    salary: '$65,000 - $95,000'
  }, {
    title: 'Consultant',
    desc: `Provide expert advice on ${programName.toLowerCase()} to businesses and clients.`,
    salary: '$70,000 - $110,000'
  }];
  const careerMap: Record<string, any[]> = {
    '1': [{
      title: 'Financial Analyst',
      desc: 'Guide businesses and individuals in making investment decisions.',
      salary: '$70,000 - $100,000'
    }, {
      title: 'Marketing Manager',
      desc: 'Estimate the demand for products and services and identify potential markets.',
      salary: '$85,000 - $135,000'
    }, {
      title: 'Human Resources Specialist',
      desc: 'Recruit, screen, interview, and place workers.',
      salary: '$55,000 - $80,000'
    }, {
      title: 'Management Analyst',
      desc: "Propose ways to improve an organization's efficiency.",
      salary: '$75,000 - $115,000'
    }],
    '2': [{
      title: 'Police Officer or Detective',
      desc: 'Protect lives and property, and gather facts and evidence for criminal cases.',
      salary: '$60,000 - $90,000'
    }, {
      title: 'Probation Officer',
      desc: 'Provide social services to assist in rehabilitation of law offenders.',
      salary: '$50,000 - $75,000'
    }, {
      title: 'Information Security Analyst',
      desc: 'Plan and carry out security measures to protect computer networks.',
      salary: '$90,000 - $130,000'
    }, {
      title: 'Paralegal',
      desc: 'Support lawyers by organizing files, conducting legal research, and drafting documents.',
      salary: '$45,000 - $70,000'
    }],
    '3': [{
      title: 'Substance Abuse Counselor',
      desc: 'Advise people who suffer from alcoholism, drug addiction, or other behavioral problems.',
      salary: '$45,000 - $65,000'
    }, {
      title: 'Social Worker',
      desc: 'Help people solve and cope with problems in their everyday lives.',
      salary: '$45,000 - $70,000'
    }, {
      title: 'Market Research Analyst',
      desc: 'Study market conditions to examine potential sales of a product or service.',
      salary: '$60,000 - $90,000'
    }, {
      title: 'Human Resources Manager',
      desc: 'Plan, direct, and coordinate the administrative functions of an organization.',
      salary: '$90,000 - $130,000'
    }],
    '4': [{
      title: 'Software Developer',
      desc: 'Create the applications or systems that run on a computer or another device.',
      salary: '$95,000 - $150,000'
    }, {
      title: 'Computer Systems Analyst',
      desc: "Study an organization's current computer systems and design solutions.",
      salary: '$80,000 - $120,000'
    }, {
      title: 'Database Administrator',
      desc: 'Use specialized software to store and organize data.',
      salary: '$85,000 - $125,000'
    }, {
      title: 'Web Developer',
      desc: 'Design and create websites, ensuring performance and capacity.',
      salary: '$70,000 - $110,000'
    }],
    '5': [{
      title: 'Medical and Health Services Manager',
      desc: 'Plan, direct, and coordinate medical and health services.',
      salary: '$85,000 - $130,000'
    }, {
      title: 'Health Educator',
      desc: 'Teach people about behaviors that promote wellness.',
      salary: '$50,000 - $75,000'
    }, {
      title: 'Medical Records Specialist',
      desc: 'Compile, process, and maintain patient files.',
      salary: '$40,000 - $60,000'
    }, {
      title: 'Clinical Laboratory Technologist',
      desc: 'Collect samples and perform tests to analyze body fluids, tissue, and other substances.',
      salary: '$50,000 - $75,000'
    }],
    '10': [{
      title: 'Registered Nurse (RN)',
      desc: 'Provide and coordinate patient care and educate patients about various health conditions.',
      salary: '$70,000 - $100,000'
    }, {
      title: 'Nurse Practitioner',
      desc: 'Coordinate patient care and may provide primary and specialty healthcare.',
      salary: '$100,000 - $140,000'
    }, {
      title: 'Medical Manager',
      desc: 'Direct and coordinate the business activities of healthcare providers.',
      salary: '$90,000 - $130,000'
    }, {
      title: 'Clinical Nurse Specialist',
      desc: 'Provide direct patient care and expert consultations.',
      salary: '$95,000 - $135,000'
    }]
  };
  return careerMap[areaValue] || defaultCareers;
};
export function ProgramContentSection({
  degreeInterest,
  areaOfStudy,
  specialization
}: ProgramContentSectionProps) {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  // Lookup display names
  const degreeNameMap: Record<string, string> = {
    '1': 'Certificate',
    '2': "Associate's",
    '3': "Bachelor's",
    '4': "Master's"
  };
  const degreeName = degreeNameMap[degreeInterest] || 'Degree';
  const areaName = focusOptions.find(f => f.value === areaOfStudy)?.type || 'your field';
  const programName = specializationOptions.find(s => s.value === specialization)?.type || areaName;
  const careers = getCareersForArea(areaOfStudy, programName);
  const faqs = [{
    q: `How long does it take to complete an online ${degreeName} in ${programName}?`,
    a: `The duration depends on your chosen pace and transfer credits. Typically, an online ${degreeName} program takes anywhere from 1 to 4 years. Many online programs offer accelerated 8-week courses that allow you to finish faster than traditional 16-week semesters.`
  }, {
    q: `What are the admission requirements for a ${programName} program?`,
    a: `Admission requirements vary by school, but generally include a high school diploma or GED (for undergraduate programs) or a bachelor's degree (for graduate programs). Some programs may require minimum GPAs, letters of recommendation, or relevant work experience.`
  }, {
    q: `Is an online ${programName} degree respected by employers?`,
    a: `Yes. Employers increasingly view online degrees from accredited institutions exactly the same as traditional on-campus degrees. What matters most is the accreditation of the school and the skills you acquire, not the medium of instruction.`
  }, {
    q: `Can I transfer previous college credits toward my ${programName} degree?`,
    a: `Most accredited online universities have generous transfer credit policies. They will evaluate your previous college transcripts, military training, and sometimes even work experience to grant credits, which can significantly reduce your tuition costs and time to graduation.`
  }];
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  return <div className="bg-white border-t border-gray-200 mt-12 pt-16 pb-20" data-id="element-744" data-mp="3axkthhu045np">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-745" data-mp="3avi4900oagk1">
        {/* Program Overview */}
        <div className="max-w-4xl mx-auto text-center mb-16" data-id="element-746" data-mp="3fu1qz2n3bwdg">
          <h2 className="text-3xl font-bold text-navy mb-6" data-id="element-747" data-mp="1gklg7vd3fnjl">
            What is an Online {programName} {degreeName}?
          </h2>
          <div className="prose prose-lg text-gray-600 mx-auto leading-relaxed space-y-6" data-id="element-748" data-mp="1lkhct9jgocp4">
            <p data-id="element-749" data-mp="3ndu85jrj932t">
              Earning an online {degreeName} in {programName} prepares you with
              the specialized knowledge and practical skills needed to succeed
              in today's competitive job market. This program focuses on the
              core principles of {areaName.toLowerCase()}, combining theoretical
              foundations with real-world applications.
            </p>
            <p data-id="element-750" data-mp="34j5is5g9l18r">
              Designed for working adults and non-traditional students, online
              programs offer the flexibility to study on your own schedule
              without sacrificing quality. You'll learn from experienced
              faculty, engage with peers through digital platforms, and develop
              a comprehensive understanding of {programName.toLowerCase()} that
              you can immediately apply to your career.
            </p>
          </div>
        </div>

        {/* Career Opportunities */}
        <div className="mb-20" data-id="element-751" data-mp="26na6u11lpz25">
          <div className="text-center mb-10" data-id="element-752" data-mp="26m06f57a0esc">
            <h2 className="text-2xl font-bold text-navy mb-4" data-id="element-753" data-mp="2bmefmqkm5ury">
              Career Opportunities with a {degreeName} in {programName}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-754" data-mp="1xog3zsxogcg5">
              Graduates with a background in {programName.toLowerCase()} are
              well-positioned for a variety of rewarding roles. Here are some
              potential career paths:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-755" data-mp="2ubugxg44hr9f">
            {careers.map((career, idx) => <div key={idx} className="bg-gray-50 rounded-xl p-6 border border-gray-100 hover:shadow-md transition-shadow duration-200 flex flex-col h-full items-center justify-start" data-id="element-756" data-mp="2gl7scgs2thr2" data-mpi={idx}>
                <div className="w-12 h-12 bg-navy/10 rounded-lg flex items-center justify-center mb-4 text-navy" data-id="element-757" data-mp="399mva4xxo9m1">
                  <BriefcaseIcon className="w-6 h-6" data-id="element-758" data-mp="3e2og0tktn3dj" />
                </div>
                <h3 className="text-lg font-bold text-navy mb-2 leading-tight" data-id="element-759" data-mp="k13ow7kfiwxp">
                  {career.title}
                </h3>
                <p className="text-sm text-gray-600 mb-6 flex-grow" data-id="element-760" data-mp="ox2hk7csj13x">
                  {career.desc}
                </p>
                <div className="pt-4 border-t border-gray-200 mt-auto" data-id="element-761" data-mp="f0z5a8o85m39">
                  <div className="flex items-center text-sm font-semibold text-gray-800" data-id="element-762" data-mp="ez5y8s9p8zuq">
                    <DollarSignIcon className="w-4 h-4 text-navy-light mr-1" data-id="element-763" data-mp="17zn1yx25ds0" />
                    {career.salary}
                  </div>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-764" data-mp="3ngso4u6139nh">
                    Estimated Annual Salary
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Why Choose Online */}
        <div className="mb-20 bg-gray-50 rounded-2xl p-8 sm:p-12 border border-gray-200" data-id="element-765" data-mp="3efui66c14s29">
          <div className="text-center mb-12" data-id="element-766" data-mp="3jfgsa6wk9s6u">
            <h2 className="text-2xl sm:text-3xl font-bold text-navy mb-4" data-id="element-767" data-mp="3jee5ob69ubwq">
              Why Earn Your {programName} {degreeName} Online?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-768" data-mp="2uv40rmw38llz">
              Online education has transformed how adults achieve their academic
              goals, offering distinct advantages over traditional on-campus
              learning.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-769" data-mp="3is9l2uetdaek">
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-id="element-770" data-mp="3iqf4ttehm6gt">
              <div className="w-12 h-12 bg-navy/10 rounded-lg flex items-center justify-center mb-4" data-id="element-771" data-mp="3dsn90oaq207c">
                <ClockIcon className="w-6 h-6 text-navy" data-id="element-772" data-mp="3sh9p4s86avzm" />
              </div>
              <h3 className="text-lg font-bold text-navy mb-2" data-id="element-773" data-mp="3nk18iw2ob00h">
                Ultimate Flexibility
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-774" data-mp="5dmsmrkugfzg">
                Study when and where it works for you. Balance your education
                with your current job, family responsibilities, and personal
                life without commuting.
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-id="element-775" data-mp="2z5uxf8ecydve">
              <div className="w-12 h-12 bg-navy/10 rounded-lg flex items-center justify-center mb-4" data-id="element-776" data-mp="33652miubt097">
                <TrendingUpIcon className="w-6 h-6 text-navy" data-id="element-777" data-mp="37z7wks30mbpx" />
              </div>
              <h3 className="text-lg font-bold text-navy mb-2" data-id="element-778" data-mp="3cvolhoi1gu42">
                Career Advancement
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-779" data-mp="3hu9lpnxajdsi">
                Apply what you learn immediately to your current role. Show
                employers your dedication and time-management skills by
                completing a rigorous online program.
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-id="element-780" data-mp="1sipk52wo7pu4">
              <div className="w-12 h-12 bg-navy/10 rounded-lg flex items-center justify-center mb-4" data-id="element-781" data-mp="1wj01tctts4ot">
                <GlobeIcon className="w-6 h-6 text-navy" data-id="element-782" data-mp="1p0jonqmnjd3n" />
              </div>
              <h3 className="text-lg font-bold text-navy mb-2" data-id="element-783" data-mp="1k77eglipz8uf">
                Broader Options
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-784" data-mp="1f9ys08qjgind">
                Don't limit yourself to local schools. Access top-tier{' '}
                {programName} programs from accredited universities nationwide,
                finding the perfect fit for your goals.
              </p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto" data-id="element-785" data-mp="1ywdci65tmt0q">
          <div className="text-center mb-10" data-id="element-786" data-mp="1yvbqj6n9om48">
            <h2 className="text-2xl font-bold text-navy mb-4" data-id="element-787" data-mp="1tyl9afzq7kh8">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600" data-id="element-788" data-mp="nj16ppqi40bx">
              Common questions about earning your {programName} degree online.
            </p>
          </div>

          <div className="space-y-4" data-id="element-789" data-mp="26fkx5yjbwl94">
            {faqs.map((faq, index) => <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm transition-all duration-200" data-id="element-790" data-mp="26e1drz2dlu34" data-mpi={index}>
                <button className="w-full px-6 py-4 text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-inset focus:ring-navy" onClick={() => toggleFaq(index)} aria-expanded={openFaqIndex === index} data-id="element-791" data-mp="1xf6aauqpuy3i">
                  <span className="font-semibold text-navy pr-8" data-id="element-792" data-mp="2p75ajtaw4n1y">{faq.q}</span>
                  {openFaqIndex === index ? <ChevronUpIcon className="w-5 h-5 text-gray-500 flex-shrink-0" data-id="element-793" data-mp="2u3vu4qlwm0s4" /> : <ChevronDownIcon className="w-5 h-5 text-gray-500 flex-shrink-0" data-id="element-794" data-mp="2g5xezizre0bk" />}
                </button>

                {openFaqIndex === index && <div className="px-6 pb-5 pt-0" data-id="element-795" data-mp="2l2xltw4fn73q">
                    <p className="text-gray-600 text-sm leading-relaxed border-t border-gray-100 pt-4 mt-2" data-id="element-796" data-mp="14qiiegbgpagz">
                      {faq.a}
                    </p>
                  </div>}
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}